import React from 'react';
import { Flag, CheckCircle, XCircle, Search, Filter, AlertTriangle, Calendar, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hasVideoFile, resolveThumbnailUrl } from '../../utils/hlsPlayer';

// Admin dashboard "moderation" tab (extracted from AdminPage).
export const AdminModerationTab = ({
  dateFilter,
  filteredPendingVideos,
  handleApproveVideo,
  handleBulkApprove,
  handleBulkReject,
  handleRejectVideo,
  resetFilters,
  searchQuery,
  selectAll,
  selectedItems,
  setDateFilter,
  setPreviewVideo,
  setSearchQuery,
  toggleSelectItem,
  videos,
}) => (
  <div className="space-y-4">
    {/* Filters Bar */}
    <div className="flex flex-wrap items-center gap-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
      <div className="flex items-center gap-2 text-zinc-400">
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">Filters:</span>
      </div>

      {/* Search */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Search pending videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />
      </div>

      {/* Date Filter */}
      <Select value={dateFilter} onValueChange={setDateFilter}>
        <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <Calendar className="w-4 h-4 mr-2" />
          <SelectValue placeholder="Date" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="all" className="text-white">All Time</SelectItem>
          <SelectItem value="today" className="text-white">Today</SelectItem>
          <SelectItem value="week" className="text-white">This Week</SelectItem>
          <SelectItem value="month" className="text-white">This Month</SelectItem>
        </SelectContent>
      </Select>

      <Button 
        variant="outline" 
        size="sm" 
        onClick={resetFilters}
        className="bg-transparent border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white"
      >
        Reset
      </Button>

      <span className="text-xs text-zinc-500 ml-auto">{filteredPendingVideos.length} pending</span>
    </div>

    {/* Bulk Actions */}
    {selectedItems.length > 0 && (
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
        <span className="text-orange-400">{selectedItems.length} items selected</span>
        <div className="flex gap-2">
          <button 
            onClick={handleBulkApprove}
            className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
          >
            Approve All
          </button>
          <button 
            onClick={handleBulkReject}
            className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
          >
            Reject All
          </button>
        </div>
      </div>
    )}

    {/* Pending Videos */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="font-semibold">Pending Review ({filteredPendingVideos.length})</h3>
        {filteredPendingVideos.length > 0 && (
          <button 
            onClick={() => selectAll(filteredPendingVideos)}
            className="text-sm text-orange-400 hover:text-orange-300"
          >
            {selectedItems.length === filteredPendingVideos.length ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {filteredPendingVideos.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-zinc-400">No videos pending review</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-800">
          {filteredPendingVideos.map((video) => (
            <div key={video.id} className="px-5 py-4 flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedItems.includes(video.id)}
                onChange={() => toggleSelectItem(video.id)}
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
              />
              <div className="w-20 h-12 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                {/* Use shared resolver so Bunny videos (which use
                    thumbnail_url / custom_thumbnail_url) render the
                    same as legacy MP4s (thumbnail_path). Falling back
                    to the AUDIVUE branded placeholder keeps the row
                    from showing a blank grey box. */}
                <img
                  src={resolveThumbnailUrl(video)}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{video.title}</p>
                <p className="text-sm text-zinc-500">by {video.artist || video.username}</p>
                {video.ai_flags?.song_check?.is_song === false && (
                  <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-amber-300">
                        AI flagged: not a music track ({Math.round((video.ai_flags.song_check.confidence || 0) * 100)}% confidence)
                      </p>
                      <p className="text-xs text-amber-200/70 mt-0.5">{video.ai_flags.song_check.reason}</p>
                      {video.ai_flags.song_check.transcript_preview && (
                        <p className="text-[11px] text-zinc-500 italic mt-1 line-clamp-2" title={video.ai_flags.song_check.transcript_preview}>
                          "{video.ai_flags.song_check.transcript_preview}…"
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewVideo(video)}
                  className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!hasVideoFile(video)}
                  title={hasVideoFile(video) ? 'Watch video' : 'No video file available'}
                  data-testid={`preview-${video.id}`}
                >
                  <Play className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleApproveVideo(video.id)}
                  className="p-2 rounded-lg bg-green-500/20 text-green-500 hover:bg-green-500/30"
                  data-testid={`approve-${video.id}`}
                >
                  <CheckCircle className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleRejectVideo(video.id)}
                  className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30"
                  data-testid={`reject-${video.id}`}
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Flagged Content */}
    <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800">
        <h3 className="font-semibold flex items-center gap-2">
          <Flag className="w-4 h-4 text-red-500" />
          Flagged Content
        </h3>
      </div>
      <div className="px-5 py-12 text-center">
        <Flag className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-400">No flagged content</p>
      </div>
    </div>
  </div>
);
